import { useState, useEffect, useRef, useCallback } from 'react';
import { useDataProvider } from 'react-admin';
import { commentImageBlobCache, getCacheKey } from './useCommentImages.js';

const resolveIsServiceInspection = (resource, serviceInspectionId, serviceRequestId) => {
    if (resource) return resource === 'service-inspection';
    if (serviceInspectionId && !serviceRequestId) return true;
    if (serviceRequestId && !serviceInspectionId) return false;
    return globalThis.location.pathname.includes('/service-inspection');
};

const isLocalPreviewImage = image => String(image?.changeId ?? '').startsWith('temp-');

const EMPTY_BLOB_URLS = Object.freeze({});

export const useFullImageLoader = ({ images, currentIndex, serviceRequestId, serviceInspectionId, enabled = true, resource, blobUrls = EMPTY_BLOB_URLS }) => {
    const [fullImageBlobUrls, setFullImageBlobUrls] = useState({});
    const [imageErrors, setImageErrors] = useState({});
    const [loadingIndexes, setLoadingIndexes] = useState({});
    const loadedBlobUrlsRef = useRef(new Set());
    const dataProvider = useDataProvider();
    const isServiceInspection = resolveIsServiceInspection(resource, serviceInspectionId, serviceRequestId);

    const applyLocalPreview = useCallback((index, blobUrl) => {
        if (!blobUrl) return false;

        setFullImageBlobUrls(prev => ({
            ...prev,
            [index]: blobUrl,
        }));
        setLoadingIndexes(prev => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
        setImageErrors(prev => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
        return true;
    }, []);

    const loadFullImageForIndex = useCallback(
        async index => {
            if (!images?.[index]) return;

            const image = images[index];

            if (isLocalPreviewImage(image) && applyLocalPreview(index, blobUrls[index])) {
                return;
            }

            const cacheKey = getCacheKey(serviceRequestId, serviceInspectionId, image.changeId, image.imageName, false, isServiceInspection);
            const currentLoadedBlobUrls = loadedBlobUrlsRef.current;

            if (commentImageBlobCache.has(cacheKey)) {
                const cachedUrl = commentImageBlobCache.get(cacheKey);
                currentLoadedBlobUrls.add(cachedUrl);
                setFullImageBlobUrls(prev => ({
                    ...prev,
                    [index]: cachedUrl,
                }));
                setLoadingIndexes(prev => {
                    if (!prev[index]) return prev;
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
                setImageErrors(prev => {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
                return;
            }

            try {
                setLoadingIndexes(prev => (prev[index] ? prev : { ...prev, [index]: true }));
                const relativeUrl = `/${isServiceInspection ? 'service-inspection' : 'service-request'}/${isServiceInspection ? serviceInspectionId : serviceRequestId}/change/${image.changeId}/${encodeURIComponent(image.imageName)}`;
                const blob = await dataProvider.getBlob(relativeUrl);
                const blobUrl = URL.createObjectURL(blob);

                commentImageBlobCache.set(cacheKey, blobUrl);
                currentLoadedBlobUrls.add(blobUrl);

                setFullImageBlobUrls(prev => ({
                    ...prev,
                    [index]: blobUrl,
                }));
                setImageErrors(prev => {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
            } catch (err) {
                console.error(`Error loading full image ${index}:`, err);
                setImageErrors(prev => ({
                    ...prev,
                    [index]: true,
                }));
            } finally {
                setLoadingIndexes(prev => {
                    if (!prev[index]) return prev;
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
            }
        },
        [images, serviceRequestId, serviceInspectionId, dataProvider, isServiceInspection, blobUrls, applyLocalPreview],
    );

    useEffect(() => {
        if (!enabled || !images || images.length === 0) {
            setFullImageBlobUrls({});
            setImageErrors({});
            setLoadingIndexes({});
            return;
        }

        const isFirst = currentIndex === 0;
        const isLast = currentIndex === images.length - 1;
        const prevIndex = currentIndex - 1;
        const nextIndex = currentIndex + 1;

        if (images[currentIndex]) {
            loadFullImageForIndex(currentIndex);
        }

        if (!isFirst && images[prevIndex]) {
            loadFullImageForIndex(prevIndex);
        }

        if (!isLast && images[nextIndex]) {
            loadFullImageForIndex(nextIndex);
        }
    }, [enabled, currentIndex, images, loadFullImageForIndex]);

    useEffect(() => {
        const currentLoadedBlobUrls = loadedBlobUrlsRef.current;
        return () => {
            const cacheValueSet = new Set(commentImageBlobCache.values());
            Array.from(currentLoadedBlobUrls).forEach(blobUrl => {
                if (blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
                    const isInCache = cacheValueSet.has(blobUrl);
                    if (!isInCache) {
                        URL.revokeObjectURL(blobUrl);
                    }
                }
            });
            currentLoadedBlobUrls.clear();
        };
    }, []);

    return { fullImageBlobUrls, imageErrors, loadingIndexes, loadFullImageForIndex };
};
